import React, { useEffect, useRef } from "react";
import { Button } from "@mui/material";
import {
  ruleAccent,
  standingRuleLabel,
  signedDifference,
  formatGoalPair,
} from "./CompactLeagueTable";
import "./TeamLeagueWorkspace.css";
export default function WorkspaceStandingsTable({
  goalSeparator,
  rows,
  data,
  visibleColumns,
  visibleMetrics,
  showStatistics,
  groups,
  columnClass,
  digits,
  labels,
  rulesFor,
  fmt,
  t,
  en,
  onTeam,
  selectedTeam,
  extraColumns = [],
  perGame = false,
}) {
  const pair=(a,b,f)=>{const value=formatGoalPair(a,b,f);return goalSeparator?value.replace(/[−–]/g,goalSeparator):value;};
  const root = useRef(null);
  const sum = (values) => values.length && values.every(Number.isFinite) ? values.reduce((a,b) => a+b,0) : null;
  const total = (key) => sum(rows.map(r => {
    const value=r[key];
    if (!Number.isFinite(value)) return null;
    if (!perGame || key==="played") return value;
    const count=["won","draw","lost","points"].includes(key) ? r.outcomeCount : r.played;
    return Number.isFinite(count) ? value*count : null;
  }));
  const mean = (values) => { const value=sum(values); return Number.isFinite(value) ? value/values.length : null; };
  const shortLabel = (label) => label.replace(/Ergebnismodell · gespeichert/g, "EM · gespeichert").replace(/Statistikmodell · gespeichert/g, "SM · gespeichert").replace(/Results model · saved/g,"RM · saved").replace(/Statistics model · saved/g,"SM · saved").replace(/Bisher erwartet/g,"Erwartet");

  useEffect(() => {
    const fit = () => { const el=root.current; if(el) el.style.maxHeight = `${Math.max(180, window.innerHeight - Math.max(0,el.getBoundingClientRect().top) - 24)}px`; };
    const frame=requestAnimationFrame(fit);
    window.addEventListener("resize",fit);
    window.addEventListener("scroll",fit,true);
    return () => { cancelAnimationFrame(frame); window.removeEventListener("resize",fit); window.removeEventListener("scroll",fit,true); };
  }, [rows, extraColumns.length, showStatistics]);
  useEffect(() => {
    if (selectedTeam)
      root.current
        ?.querySelector(`[data-team-id="${Number(selectedTeam)}"]`)
        ?.scrollIntoView?.({ block: "nearest" });
  }, [selectedTeam]);
  return (
    <div ref={root} className="tl-table">
      <table
        style={{
          width:
            40 +
            260 +
            extraColumns.length * 150 +
            visibleColumns.reduce(
              (n, k) =>
                n +
                (["played", "won", "draw", "lost"].includes(k)
                  ? 48
                  : k === "goals"
                  ? 104
                  : 88),
              0
            ) +
            (showStatistics ? groups.length * visibleMetrics.length * 132 : 0) +
            (onTeam ? 120 : 0),
        }}
      >
        <colgroup>
          <col style={{ width: 40 }} />
          <col style={{ width: 260 }} />
          {visibleColumns.map((k) => (
            <col
              key={k}
              style={{
                width: ["played", "won", "draw", "lost"].includes(k)
                  ? 48
                  : k === "goals"
                  ? 104
                  : 88,
              }}
            />
          ))}
          {showStatistics &&
            groups.flatMap((g, n) =>
              visibleMetrics.map((k) => (
                <col key={g + k} style={{ width: 132 }} />
              ))
            )}
          {extraColumns.map((c) => (
            <col key={c.key} style={{ width: 150 }} />
          ))}
          {onTeam && <col style={{ width: 120 }} />}
        </colgroup>
        <thead>
          <tr>
            <th
              rowSpan={showStatistics && visibleMetrics.length ? 2 : 1}
              className="tl-position"
            >
              #
            </th>
            <th
              rowSpan={showStatistics && visibleMetrics.length ? 2 : 1}
              className="tl-team"
            >
              {t("Mannschaft", "Team")}
            </th>
            {visibleColumns.map((k) => (
              <th
                rowSpan={showStatistics && visibleMetrics.length ? 2 : 1}
                key={k}
                title={labels[k]}
                className={columnClass(k)}
              >
                {{
                  played: t("Sp", "P"),
                  won: t("S", "W"),
                  draw: t("R", "D"),
                  lost: t("N", "L"),
                }[k] || labels[k]}
              </th>
            ))}
            {showStatistics &&
              visibleMetrics.length > 0 &&
              groups.map((label, n) => (
                <th
                  key={label}
                  colSpan={visibleMetrics.length}
                  className={`tl-stat-group tl-stat-${n}`}
                >
                  {label}
                </th>
              ))}
            {extraColumns.map((c) => (
              <th
                key={c.key}
                title={c.label}
                className="tl-extra-heading"
                rowSpan={showStatistics && visibleMetrics.length ? 2 : 1}
              >
                <abbr title={c.label}>{shortLabel(c.label)}</abbr>
              </th>
            ))}
            {onTeam && (
              <th
                rowSpan={showStatistics && visibleMetrics.length ? 2 : 1}
                className="tl-open"
              >
                {t("Öffnen", "Open")}
              </th>
            )}
          </tr>
          {showStatistics && visibleMetrics.length > 0 && (
            <tr>
              {groups.map((label, n) =>
                visibleMetrics.map((k, i) => (
                  <th
                    key={label + k}
                    className={`tl-stat tl-stat-${n} ${
                      i === 0 ? "tl-stat-start" : ""
                    }`}
                  >
                    {k === "quotient" ? (
                      <>
                        {t("Spielstärke", "Strength")}
                        <small>{t("Quotient", "Ratio")}</small>
                      </>
                    ) : (
                      <>
                        {t("Torniveau", "Goal level")}
                        <small>{t("Toreschnitt", "Goals/game")}</small>
                      </>
                    )}
                  </th>
                ))
              )}
            </tr>
          )}
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.teamId}
              data-team-id={r.teamId}
              className={
                Number(selectedTeam) === r.teamId ? "tl-selected" : undefined
              }
              style={{
                borderLeft: "4px solid " + ruleAccent(rulesFor(r.teamId)),
              }}
              title={rulesFor(r.teamId).map(standingRuleLabel).join(" · ")}
            >
              <td className="tl-position">{fmt(r.position, 0)}</td>
              <th scope="row" className="tl-team">
                {data.team_images?.[r.teamId] ? (
                  <img src={data.team_images[r.teamId]} alt="" />
                ) : (
                  <span aria-hidden="true">⚽ </span>
                )}
                {r.name || t("Name nicht verfügbar", "Name unavailable")}
                {(data.transitions || []).some(
                  (x) =>
                    Number(x.team_id) === r.teamId &&
                    x.transition_type === "promoted"
                ) && (
                  <span
                    title={t(
                      "Aufsteiger aus der Vorsaison",
                      "Promoted from previous season"
                    )}
                  >
                    {" "}
                    ↑
                  </span>
                )}
                {(data.transitions || []).some(
                  (x) =>
                    Number(x.team_id) === r.teamId &&
                    x.transition_type === "relegated"
                ) && (
                  <span
                    title={t(
                      "Absteiger aus der Vorsaison",
                      "Relegated from previous season"
                    )}
                  >
                    {" "}
                    ↓
                  </span>
                )}
                {r.coverage && (
                  <small style={{ display: "block" }}>{r.coverage}</small>
                )}
              </th>
              {visibleColumns.map((k) => (
                <td key={k} className={columnClass(k)}>
                  {k === "goals"
                    ? pair(r.goalsScored, r.goalsAgainst, (v) =>
                        fmt(v, digits(k))
                      )
                    : k === "difference"
                    ? signedDifference(r[k], digits(k), en ? "en-GB" : "de-DE")
                    : fmt(r[k], digits(k))}
                </td>
              ))}
              {showStatistics &&
                groups.map((label, n) =>
                  visibleMetrics.map((k, i) => {
                    const value =
                      n === 0
                        ? r[k]
                        : r[n === 1 ? "result" : "statistics"]?.[
                            k === "quotient" ? "total_strength" : "total_goals"
                          ];
                    const zeroAgainst =
                      n === 0 && k === "quotient" && r.goalsAgainst === 0;
                    return (
                      <td
                        key={label + k}
                        className={`tl-stat tl-stat-${n} ${
                          i === 0 ? "tl-stat-start" : ""
                        }`}
                        title={
                          zeroAgainst
                            ? t(
                                "Keine Gegentore: Quotient nicht definiert.",
                                "No goals conceded: ratio undefined."
                              )
                            : undefined
                        }
                      >
                        {zeroAgainst
                          ? t("— (0 Gegentore)", "— (0 conceded)")
                          : fmt(value, 2)}
                      </td>
                    );
                  })
                )}
              {extraColumns.map((c) => (
                <td key={c.key}>{fmt(c.value(r.teamId), 2)}</td>
              ))}
              {onTeam && (
                <td className="tl-open">
                  <Button
                    disabled={!onTeam}
                    onClick={() => {
                      onTeam?.(r.teamId);
                    }}
                  >
                    {t("Team ansehen", "View team")}
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th className="tl-position">Σ</th><th className="tl-team">{t("Summe / Liga", "Total / league")}</th>
            {visibleColumns.map(k => <td key={k}>{k === "goals" ? pair(total("goalsScored"),total("goalsAgainst"),v=>fmt(v,digits("goals"))) : fmt(total(k),digits(k))}</td>)}
            {showStatistics && groups.map((g,n)=>visibleMetrics.map(k=>{
              const a=total("goalsScored"),b=total("goalsAgainst"),games=total("played");
              const v=n===0 ? (k==="quotient" ? (b>0?a/b:null) : (games>0?(a+b)/games:null)) : null;
              return <td key={g+k} title={n===0?t("Aus ungerundeten Torsummen; Spiele zählen je Mannschaft.","From unrounded goal totals; appearances counted per team."):t("Quotienten und Toreschnitte werden nicht addiert.","Ratios and goal levels are not added.")}>{fmt(v,2)}</td>;
            }))}
            {extraColumns.map(c=><td key={c.key} title={t("Mittelwert der angezeigten Teams; keine Summe von Raten.","Mean of displayed teams; rates are not summed.")}>Ø {fmt(mean(rows.map(r=>c.value(r.teamId))),2)}</td>)}
            {onTeam && <td/>}
          </tr>
          <tr>
            <th className="tl-position">Ø</th><th className="tl-team">{t("Je Mannschaft", "Per team")}</th>
            {visibleColumns.map(k=><td key={k}>{k==="goals" ? pair(mean(rows.map(r=>r.goalsScored)),mean(rows.map(r=>r.goalsAgainst)),v=>fmt(v,2)) : fmt(mean(rows.map(r=>r[k])),2)}</td>)}
            {showStatistics && groups.map((g,n)=>visibleMetrics.map(k=><td key={g+k}>{n===0?'—':fmt(mean(rows.map(r=>r[n===1?"result":"statistics"]?.[k==="quotient"?"total_strength":"total_goals"])),2)}</td>))}
            {extraColumns.map(c=><td key={c.key}>—</td>)}{onTeam&&<td/>}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
